import { Stack, Typography } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import z from "zod";
import { TabsComponent } from "@/components/core/tabs-component";
import { SettingsBilling } from "@/components/settings/billing";
import { SettingsDocuments } from "@/components/settings/documents";
import { SettingsEmails } from "@/components/settings/emails";
import { SettingsIntegrations } from "@/components/settings/integrations";
import { SettingsPolicies } from "@/components/settings/policies";
import { SettingsNotifications } from "@/components/settings/notifications";
import { SettingsSidebarNotifications } from "@/components/settings/sidebar-notifications";
import { queryClient } from "@/helpers/tanstack-client";
import { getStatusQueryKey, status } from "@/models/backend";

const tabs = [
  "emails",
  "notifications",
  "documents",
  "policies",
  "integrations",
  "billing",
] as const;

const schema = z.object({
  tab: z.enum(tabs).default("emails"),
  document: z.uuid().optional(),
  sessionId: z.string().optional(),
  oauth_error: z.string().optional(),
  provider: z.string().optional(),
  api: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/_contained/settings")({
  component: RouteComponent,
  validateSearch: schema,
  loaderDeps: ({ search: { tab } }) => ({ tab }),
  loader: async ({ deps: { tab } }) => {
    if (tab === "integrations") {
      await queryClient.ensureQueryData({
        queryKey: getStatusQueryKey(),
        queryFn: status,
      });
    }
  },
});
function RouteComponent() {
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();

  const tabObjs = [
    { id: tabs[0], label: "Emails", component: <SettingsEmails /> },
    {
      id: tabs[1],
      label: "Notifications",
      component: (
        <Stack spacing={4} sx={{ alignItems: "start" }}>
          <SettingsNotifications />
          <SettingsSidebarNotifications />
        </Stack>
      ),
    },
    {
      id: tabs[2],
      label: "Documents",
      component: <SettingsDocuments />,
    },
    { id: tabs[3], label: "Policies", component: <SettingsPolicies /> },
    {
      id: tabs[4],
      label: "Integrations",
      component: <SettingsIntegrations />,
    },
    {
      id: tabs[5],
      label: "Billing",
      component: <SettingsBilling />,
    },
  ];

  return (
    <Stack spacing={2}>
      <Typography variant="h3">Settings</Typography>
      <TabsComponent
        tabs={tabObjs}
        selectedTab={tabObjs.findIndex((tabObj) => tabObj.id === tab)}
        onSelectedTab={(idx) => {
          navigate({ search: { tab: tabObjs[idx].id } });
        }}
      />
    </Stack>
  );
}
